import React, { useState } from 'react';
import { 
    DndContext, closestCorners, PointerSensor, useSensor, useSensors, DragOverlay 
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Play, ArrowRight, Info, GitBranch, List } from 'lucide-react';
import EliminationBracket from './EliminationBracket';
import Swal from 'sweetalert2';

// --- 1. COMPOSANT ATHLÈTE ---
function SortableAthlete({ athlete, pouleId }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
        id: `ath-${athlete.id}`,
        data: { type: 'athlete', athlete, fromPoule: pouleId }
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        fontSize: '13px',
        padding: '8px 12px',
        backgroundColor: 'white',
        border: '1px solid #edf2f7',
        borderRadius: '6px',
        marginBottom: '4px',
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: isDragging ? 10 : 1
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#cbd5e0' }} />
            {athlete.seed === 1 && <span style={{ fontSize: '10px', fontWeight: '800', color: '#744210', backgroundColor: '#f6e05e', borderRadius: '4px', padding: '1px 4px', flexShrink: 0 }}>#1</span>}
            {athlete.seed === 2 && <span style={{ fontSize: '10px', fontWeight: '800', color: '#4a5568', backgroundColor: '#e2e8f0', borderRadius: '4px', padding: '1px 4px', flexShrink: 0 }}>#2</span>}
            <span style={{ fontWeight: '500', flex: 1 }}>{athlete.name}</span>
            <span style={{ fontSize: '11px', color: '#a0aec0' }}>{athlete.club}</span>
        </div>
    );
}

// --- 2. COMPOSANT POULE ---
function SortablePoule({ poule, isOverlay = false }) {
    const [showBracket, setShowBracket] = useState(false);

    const isElimination = poule.type === 'elimination' ||
                         poule.format === 'elimination' ||
                         poule.format === 'elimination_directe' ||
                         poule.format === 'finale_directe';

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: poule.id,
        data: { type: 'poule', poule }
    });

    const badgeColor = isElimination ? '#3182ce' : '#38a169';
    const badgeBg = isElimination ? '#ebf8ff' : '#f0fff4';

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging && !isOverlay ? 0.3 : 1,
        backgroundColor: "#ffffff",
        padding: "15px",
        borderRadius: "10px",
        marginBottom: "12px",
        boxShadow: isOverlay ? "0 10px 20px rgba(0,0,0,0.15)" : "0 2px 4px rgba(0,0,0,0.05)",
        border: isOverlay ? `2px solid ${badgeColor}` : "1px solid #e2e8f0",
        zIndex: isOverlay ? 1000 : 1,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div {...attributes} {...listeners} style={{ cursor: 'grab', padding: '4px' }}>
                        <GripVertical size={20} color="#cbd5e0" />
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#2d3748' }}>{poule.category_name}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isElimination && poule.format !== 'finale_directe' && (
                        <button
                            onClick={() => setShowBracket(v => !v)}
                            title={showBracket ? "Vue liste" : "Vue bracket"}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '4px',
                                padding: '3px 8px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                                fontSize: '10px', fontWeight: '700',
                                backgroundColor: showBracket ? '#3182ce' : '#edf2f7',
                                color: showBracket ? 'white' : '#4a5568',
                                transition: '0.2s'
                            }}
                        >
                            {showBracket ? <List size={12} /> : <GitBranch size={12} />}
                            {showBracket ? 'Liste' : 'Bracket'}
                        </button>
                    )}
                    <span style={{
                        fontSize: '10px',
                        fontWeight: '900',
                        color: badgeColor,
                        backgroundColor: badgeBg,
                        padding: '3px 8px',
                        borderRadius: '5px',
                        textTransform: 'uppercase'
                    }}>
                        {poule.poule_label}
                    </span>
                </div>
            </div>

            {isElimination && showBracket ? (
                <div style={{ overflowX: 'auto' }}>
                    <EliminationBracket athletes={poule.athletes} pouleId={poule.id} />
                </div>
            ) : (
                <SortableContext items={poule.athletes.map(a => `ath-${a.id}`)} strategy={verticalListSortingStrategy}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {poule.athletes.map(ath => (
                            <SortableAthlete key={ath.id} athlete={ath} pouleId={poule.id} />
                        ))}
                    </div>
                </SortableContext>
            )}
        </div>
    );
}

// --- 3. COMPOSANT PRINCIPAL ---
export default function PlateauManager({ plateau, onGenerate, onUpdatePlateau, onFinalGenerate, currentRings, setCurrentRings }) {
    const [activeId, setActiveId] = useState(null);
    const [activeType, setActiveType] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const rings = plateau ? Object.keys(plateau) : [];

    const findContainer = (id) => {
        if (rings.includes(id)) return id;
        return rings.find((key) => plateau[key].some((p) => p.id === id || p.athletes.some(a => `ath-${a.id}` === id)));
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        setActiveType(event.active.data.current?.type);
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over || activeType !== 'poule') return;

        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(over.id);

        if (!activeContainer || !overContainer || activeContainer === overContainer) return;

        const activeItems = plateau[activeContainer];
        const overItems = plateau[overContainer];
        const activeIndex = activeItems.findIndex((p) => p.id === active.id);
        const overIndex = overItems.findIndex((p) => p.id === over.id);

        let newIndex = overIndex >= 0 ? overIndex : overItems.length;

        const newPlateau = {
            ...plateau,
            [activeContainer]: plateau[activeContainer].filter((p) => p.id !== active.id),
            [overContainer]: [
                ...plateau[overContainer].slice(0, newIndex),
                plateau[activeContainer][activeIndex],
                ...plateau[overContainer].slice(newIndex)
            ]
        };
        onUpdatePlateau(newPlateau);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setActiveType(null);
        if (!over) return;

        if (active.id.toString().startsWith('ath-') && over.id.toString().startsWith('ath-')) {
            const newPlateau = { ...plateau };
            let athleteA, pouleA, athleteB, pouleB;

            rings.forEach(r => {
                newPlateau[r].forEach(p => {
                    const idxA = p.athletes.findIndex(a => `ath-${a.id}` === active.id);
                    if (idxA !== -1) { athleteA = p.athletes[idxA]; pouleA = p; }
                    const idxB = p.athletes.findIndex(a => `ath-${a.id}` === over.id);
                    if (idxB !== -1) { athleteB = p.athletes[idxB]; pouleB = p; }
                });
            });

            if (pouleA && pouleB) {
                if (pouleA.category_id !== pouleB.category_id) {
                    Swal.fire({
                        title: 'Catégories différentes !',
                        html: `L'athlète <b>${athleteA.name}</b> ne peut pas être échangé avec un tireur de la catégorie <b>${pouleB.category_name}</b>.`,
                        icon: 'warning',
                        confirmButtonColor: '#3182ce'
                    });
                    return;
                }

                const idxA = pouleA.athletes.findIndex(a => a.id === athleteA.id);
                const idxB = pouleB.athletes.findIndex(a => a.id === athleteB.id);
                pouleA.athletes[idxA] = athleteB;
                pouleB.athletes[idxB] = athleteA;
                onUpdatePlateau(newPlateau);
                
                const Toast = Swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
                Toast.fire({ icon: 'success', title: 'Échange effectué' });
            }
            return;
        }

        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(over.id);

        if (activeContainer && overContainer && activeContainer === overContainer) {
            const activeIndex = plateau[activeContainer].findIndex((p) => p.id === active.id);
            const overIndex = plateau[overContainer].findIndex((p) => p.id === over.id);

            if (activeIndex !== overIndex) {
                const newPlateau = {
                    ...plateau,
                    [activeContainer]: arrayMove(plateau[activeContainer], activeIndex, overIndex)
                };
                onUpdatePlateau(newPlateau);
            }
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            <div style={{ ...styles.toolbar, flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <div style={{ ...styles.ringSelectorWrapper, borderRight: isMobile ? 'none' : '1px solid #edf2f7', paddingRight: isMobile ? 0 : '20px' }}>
                        <span style={styles.ringLabel}>Rings :</span>
                        <select
                            value={currentRings}
                            onChange={(e) => setCurrentRings(parseInt(e.target.value))}
                            style={styles.select}
                        >
                            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>

                    <button onClick={onGenerate} style={styles.btnSecondary}>
                        <Play size={16} /> {(!plateau || Object.keys(plateau).length === 0) ? "Générer la répartition" : "Régénérer"}
                    </button>
                </div>

                {plateau && Object.keys(plateau).length > 0 && (
                    <button onClick={onFinalGenerate} style={{ ...styles.btnPrimary, width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}>
                        Valider le Plateau <ArrowRight size={18} />
                    </button>
                )}
            </div>

            {(!plateau || Object.keys(plateau).length === 0) ? (
                <div style={styles.emptyState}>
                    <Info size={48} color="#a0aec0" />
                    <h3 style={{ color: '#4a5568' }}>Prêt pour la répartition</h3>
                    <p style={{ color: '#718096' }}>Sélectionnez le nombre de rings et lancez la génération.</p>
                </div>
            ) : (
                <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCorners} 
                    onDragStart={handleDragStart} 
                    onDragOver={handleDragOver} 
                    onDragEnd={handleDragEnd}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${rings.length}, 1fr)`, gap: '20px' }}>
                        {rings.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((ringKey) => {
                            
                            // CALCUL DYNAMIQUE DES MATCHS DU RING
                            const totalMatchsDuRing = plateau[ringKey].reduce((sum, poule) => {
                                const n = poule.athletes?.length || 0;
                                const isElim = poule.type === 'elimination' || poule.format === 'elimination' || poule.format === 'elimination_directe' || poule.format === 'finale_directe';
                                const matchs = isElim ? (n - 1) : (n * (n - 1)) / 2;
                                return sum + Math.max(0, matchs);
                            }, 0);

                            return (
                                <div key={ringKey} style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '14px', minHeight: '550px', border: '2px solid #edf2f7' }}>
                                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                        <h4 style={{ color: '#718096', margin: 0, fontSize: '14px', letterSpacing: '1px' }}>
                                            {ringKey.toUpperCase()}
                                        </h4>
                                        <div style={{ 
                                            display: 'inline-block', 
                                            marginTop: '6px', 
                                            padding: '2px 10px', 
                                            backgroundColor: '#edf2f7', 
                                            borderRadius: '10px', 
                                            fontSize: '11px', 
                                            fontWeight: '800', 
                                            color: '#4a5568' 
                                        }}>
                                            {totalMatchsDuRing} MATCHS
                                        </div>
                                    </div>

                                    <SortableContext items={plateau[ringKey].map(p => p.id)} strategy={verticalListSortingStrategy}>
                                        <div style={{ minHeight: '100px' }}>
                                            {plateau[ringKey].map((poule) => (
                                                <SortablePoule key={poule.id} poule={poule} />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </div>
                            );
                        })}
                    </div>

                    <DragOverlay>
                        {activeId ? (
                            <div style={{ cursor: 'grabbing' }}>
                                {activeType === 'poule' ? (
                                    <SortablePoule poule={rings.flatMap(r => plateau[r]).find(p => p.id === activeId)} isOverlay />
                                ) : (
                                    <div style={{ padding: '8px 15px', backgroundColor: 'white', border: '2px solid #3182ce', borderRadius: '8px', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', fontSize: '13px', fontWeight: 'bold' }}>
                                        Déplacement d'athlète...
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}
        </div>
    );
}

const styles = {
    toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 25px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
    ringSelectorWrapper: { display: 'flex', alignItems: 'center', gap: '10px', paddingRight: '20px', borderRight: '1px solid #edf2f7' },
    ringLabel: { fontSize: '14px', fontWeight: 'bold', color: '#4a5568' },
    select: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e0', fontWeight: 'bold', color: '#2c5282' },
    btnPrimary: { backgroundColor: '#48bb78', color: 'white', padding: '10px 25px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' },
    btnSecondary: { color: "#3182ce", border: '1px solid #bee3f8', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#ebf8ff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' },
    emptyState: { padding: '80px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '2px dashed #edf2f7', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }
};
